import { faKey } from "@fortawesome/pro-duotone-svg-icons"
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Input, Table, TablePaginationConfig } from "antd"
import { FilterValue, SorterResult } from "antd/es/table/interface"
import { isArray } from "lodash"
import { useState } from "react"
import { useAuthenticateToTenant } from "../authentication/authenticationQueries"
import VSpace from "../common/VSpace"
import { AdminSearchParameters, AdminSearchResult } from "./checklistApi"
import { useAdminSearch } from "./checklistQueries"
import ReportIcons from "./ReportIcons"
import { useDebouncedState } from "../../hooks/useDebouncedState"

const defaultSearchParameters: AdminSearchParameters = {
  page: 1,
  pageSize: 20,
  orderAscending: true,
  showChecklistFilter: 'hasChecklist'
}

const SuccessChecklistReport = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const { debouncedState: debouncedSearchQuery, setState: setDebouncedSearchQuery } = useDebouncedState('', 250)
  const [searchParameters, setSearchParameters] = useState<AdminSearchParameters>({ ...defaultSearchParameters })
  const adminSearchQuery = useAdminSearch({ ...searchParameters, query: debouncedSearchQuery })
  const tenantAuth = useAuthenticateToTenant()

  const results = adminSearchQuery.data?.results ?? []

  const handleTableChange = (pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AdminSearchResult>
  ) => {
    setSearchParameters({
      ...searchParameters,
      page: pagination.current ?? defaultSearchParameters.page,
      pageSize: pagination.pageSize ?? defaultSearchParameters.pageSize,
      orderBy: sorter.columnKey as string,
      orderAscending: sorter.order === 'ascend',
      showChecklistFilter: filters.showSuccessChecklist?.length === 1
        ? filters.showSuccessChecklist[0].toString()
        : undefined
    })
  }

  return <VSpace>
    <span>Success Checklist Report</span>

    <Input
      addonBefore={<FontAwesomeIcon icon={faMagnifyingGlass} />}
      placeholder="Filter tenant names"
      value={searchQuery}
      onChange={(e) => {
        setSearchQuery(e.target.value)
        setDebouncedSearchQuery(e.target.value)
      }}
    />

    <Table
      loading={adminSearchQuery.isLoading}
      dataSource={results}
      rowKey={(x) => x.tenantId}
      columns={[
        {
          title: 'Tenant',
          dataIndex: 'tenantName',
          defaultSortOrder: 'ascend',
          sorter: true,
          key: 'tenantName',
          render: (_, record) => <div style={{ display: 'flex', flexDirection: 'row', flexFlow: 'row' }}>
            <span><a href={`/#!/tenants?id=${record.tenantId}`}>{record.tenantName}</a></span>
            <span style={{ marginLeft: 'auto' }}>
              <Button onClick={() => tenantAuth.mutate(record.tenantId)}>
                <FontAwesomeIcon icon={faKey} />
              </Button>
            </span>
          </div>
        },
        {
          title: 'Show Checklist?',
          render: (_, record) => record.showSuccessChecklist ? 'YES' : 'NO',
          sorter: true,
          key: 'showSuccessChecklist',
          filters: [
            { text: 'YES', value: 'hasChecklist' },
            { text: 'NO', value: 'noChecklist' }
          ],
          defaultFilteredValue: ['hasChecklist']
        },
        {
          title: 'Last Update',
          render: (_, record) => record.checklist ? new Date(record.checklist.updatedAt).toLocaleString() : 'Never',
          sorter: true,
          key: 'updatedAt'
        },
        {
          title: 'Completed Sections',
          render: (_, record) => record.checklist?.completedSections ?? 0,
          sorter: true,
          key: 'completedSections'
        },
        {
          title: 'Completed Steps',
          render: (_, record) => record.checklist?.completedSteps ?? 0,
          sorter: true,
          key: 'completedSteps'
        },
        {
          title: 'Primary Goal',
          render: (_, record) => record.checklistPrimaryGoal ?? '',
          sorter: true,
          key: 'checklistPrimaryGoal'
        },
        {
          title: 'Primary Goal Date',
          render: (_, record) => record.checklistPrimaryGoalDate ? new Date(record.checklistPrimaryGoalDate).toDateString().toLocaleString() : '',
          sorter: true,
          key: 'checklistPrimaryGoalDate'
        },
        {
          title: 'Primary Goal Completed Date',
          render: (_, record) => record.checklistPrimaryGoalCompletedDate ? new Date(record.checklistPrimaryGoalCompletedDate).toDateString().toLocaleString() : '',
          sorter: true,
          key: 'checklistPrimaryGoalCompletedDate'
        },
        {
          render: (_, record) => <ReportIcons steps={record.checklist?.steps} />
        }
      ]}
      pagination={{
        pageSize: searchParameters.pageSize,
        total: adminSearchQuery.data?.totalCount ?? 0,
        pageSizeOptions: [20, 50, 100]
      }}
      onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, isArray(sorter) ? sorter[0] : sorter)}
    />
  </VSpace>
}

export default SuccessChecklistReport
